import { SIZE, VIDEO_PROVIDERS } from '@sleepstation/const'
import { parseToAst } from '@sleepstation/markdown'
import { mergeDeepRight } from 'ramda'
import { RANGE_DBAS } from '../view/const/dbas'
import { RANGE_EPWORTH } from '../view/const/epworth'
import NOTICE_TYPE from '../view/const/noticeType'
import QUERY_PARAMS from '../view/const/queryParams'
import { HUB_CARD_VARIANT } from '../view/const/variants'
import patientShortStoriesData from './data/patientShortStories'
import epworthIntroImageUrl from './images/illustrations/cards/trouble-with-sleep.webp'
import dbasIntroImageUrl from './images/illustrations/cards/img-reading-in-bed.webp'
import enoughSleepSvgUrl from './images/illustrations/cards/enoughsleep-large.svg'
import bedroomIllustrationUrl from './images/illustrations/cards/bedroom-illustration.webp'
import cardProgrammeSvgUrl from './images/illustrations/cards/img-couple-sleeping.webp'
import cardLibraryWebpUrl from './images/illustrations/cards/library-books.webp'
import thoughtsSvgUrl from './images/illustrations/cards/thoughts-large.svg'
import sleepCoachesLargeUrl from './images/illustrations/cards/card-image-sleep-coaches-photo-d.webp'
import messagingSupportUrl from './images/illustrations/cards/messaging-support-illustration.webp'
import resourcesIllustrationUrl from './images/illustrations/cards/resources.webp'
import servicesIllustrationUrl from './images/illustrations/cards/services.webp'
import supportIllustrationUrl from './images/illustrations/cards/support.webp'
import serviceWelcomeIllustrationUrl from './images/illustrations/cards/service-welcome.webp'
import nhsLogoUrl from './images/illustrations/cards/nhs-logo.svg'
import sleepCoachesSmallUrl from './images/illustrations/cards/card-image-sleep-coaches-photo-m.webp'
import sleepingIllustrationUrl from './images/illustrations/decks/welcome/sleeping-illustration.webp'
import notesIllustrationUrl from './images/illustrations/decks/welcome/notes-illustration.webp'
import readingIllustrationUrl from './images/illustrations/decks/welcome/reading-illustration.webp'
import howItWorksIllustrationXSmallUrl from './images/illustrations/how-it-works-illustration-mobile.svg'
import howItWorksIllustrationSmallUpUrl from './images/illustrations/how-it-works-illustration.svg'
import ctaComponentProductShotIllustrationSmallDownUrl from './images/illustrations/product/cta-component-product-shot-mobile.png'
import ctaComponentProductShotIllustrationmediumUpUrl from './images/illustrations/product/cta-kirstie-product-shot-hand-desktop.png'
import { ReactComponent as SleepAndEmotionsSectionIllustrationSVG } from './images/illustrations/sleep-and-emotions-illustration.svg'
import interestsCardSvgUrl from './images/illustrations/stars/stars-card-interests-deselected.svg'
import interestsCardSelectedSvgUrl from './images/illustrations/stars/stars-card-interests-selected.svg'
import thankYouSvgUrl from './images/illustrations/thank-you.svg'
import discoverYourSleepNeedsIllustrationUrl from './images/illustrations/timeline/discover-sleep-needs-illustration.svg'
import improveYourMoodIllustrationUrl from './images/illustrations/timeline/fully-supported-illustration.svg'
import improveSleepQualityIllustrationUrl from './images/illustrations/timeline/improve-sleep-quality-illustration.svg'
import liveBetterIllustrationUrl from './images/illustrations/timeline/live-better-illustration.svg'
import videoTestimonialSvgUrl from './images/video-testimonial-illustration.svg'
import writtenTestimonialSvgUrl from './images/written-testimonial-illustration.svg'

// -----------------------------------------------------------------------------
// Const
// -----------------------------------------------------------------------------

// TODO: Extract to routing
const DASHBOARD_URL = `#{railsApp}/dashboard/insomnia`
const FEEDBACK_THANK_YOU_URL = `/feedback/thank-you`
const FEEDBACK_END_OF_THERAPY_THANK_YOU_URL = `/feedback/end-of-therapy/thank-you`

const BACK_TO_DASHBOARD_LINK = {
  title: `Back to dashboard`,
  'data-cy': `back-to-dashboard-link`,
  path: DASHBOARD_URL,
  forceOpenInSameWindow: true,
  kind: `secondary`,
  size: SIZE.SMALL,
}

const BACK_TO_OPTIONS_LINK = {
  title: `Back to options`,
  'data-cy': `back-to-options-link`,
  path: FEEDBACK_END_OF_THERAPY_THANK_YOU_URL,
  forceOpenInSameWindow: true,
  icon: `previousAlt`,
  kind: `secondary`,
  size: SIZE.SMALL,
}

const FEEDBACK_END_OF_THERAPY_CONSENT_STATEMENT = `We’re so glad that you’ve had a positive experience. Would you like to share your experience?

By sharing you could help others get the help they need for their sleep. If you’d like to tell your Sleepstation story and help more people to achieve better sleep then we’d love to talk to you. If you’re happy to have a chat then our media team is waiting by the phone!`

// -----------------------------------------------------------------------------
// Const
// -----------------------------------------------------------------------------

// TODO: Extract to routing
const CHECKOUT_LINK_TEMPLATE = `#{railsApp}/checkout?key=#{key}`

const packageWithFeaturesCtaSectionBase = {
  cardData: {
    title: `Sleep support`,
    subtitleTemplate: `Unlock now for #{priceWithCurrency}`,
    features: [
      { value: `Six-step sleep plan` },
      { value: `Full access to our sleep team` },
      { value: `Dedicated support and guidance` },
      { value: `Advice from leading sleep experts` },
      { value: `New techniques every week` },
      { value: `Feel the benefits of great sleep.` },
    ],
  },
  linkLabelTemplate: `Sleep well again for #{priceWithCurrency}`,
  linkUrlTemplate: CHECKOUT_LINK_TEMPLATE,
}

const CONTENT = {
  // -------------------------------------------------------------------------
  // App
  // -------------------------------------------------------------------------
  app: {
    header: {
      accountNav: {
        logout: {
          label: `Log out`,
        },
      },
    },
  },
  template: [
    {
      route: `/feedback/thank-you`,
      data: {
        page: {
          content: {
            illustration: {
              url: thankYouSvgUrl,
              alt: `Illustration of a flag saying 'Thank you'`,
            },
            body: `Thank you. Our teams review all feedback and use it to improve our service. If you have a specific query or a concern that you need to discuss with your support team, please send them a message.`,
            backToDashboardLink: BACK_TO_DASHBOARD_LINK,
          },
        },
      },
    },
    {
      route: `/feedback/written-testimonial`,
      data: {
        page: {
          content: {
            title: `Write a testimonial`,
            bodyAst: parseToAst(
              `Write a short piece about your Sleepstation experience for us to share on our website and social media.`
            ),
            ideasCard: {
              title: `Ideas`,
              iconName: `ideas`,
              items: [
                `What was the problem you were having before you discovered Sleepsation?`,
                `What did the frustration feel like as you tried to solve the problem?`,
                `Take us to the moment you realised Sleepstation was actually working to solve your problem?`,
                `Tell us what life is like now your problem is solved or being solved?`,
                `Review your Sleepstation progress for ideas.`,
              ],
            },
            form: {
              completeUrl: FEEDBACK_THANK_YOU_URL,
            },
            footerNav: {
              primary: [BACK_TO_DASHBOARD_LINK],
              secondary: [BACK_TO_OPTIONS_LINK],
            },
          },
        },
      },
    },
    {
      route: `/feedback/video-testimonial`,
      data: {
        page: {
          content: {
            title: `Share a video`,
            bodyAst: parseToAst(
              `Share a video about your Sleepstation experience on social media. We’ll repost your video.`
            ),
            tipsCard: {
              title: `Tips`,
              iconName: `tips`,
              items: [
                `You don’t need any special equipment, the camera on your phone will work.`,
              ],
            },
            ideasCard: {
              title: `Ideas`,
              iconName: `ideas`,
              items: [
                ` What was the problem you were having before you discovered Sleepsation?`,
                `What did the frustration feel like as you tried to solve the problem?`,
                `What was different about Sleepstation?`,
                `Take us to the moment you realised Sleepstation was actually working to solve your problem?`,
                `Tell us what life is like now your problem is solved or being solved?`,
              ],
            },
            social: {
              title: `Once you’ve filmed your video share it with us`,
              pages: [
                {
                  icon: `facebook`,
                  title: `Share and tag us on Facebook`,
                  path: `https://www.facebook.com/SleepstationApp/`,
                },
                {
                  icon: `twitter`,
                  title: `Tweet it and tag @_sleepstation`,
                  path: `https://twitter.com/_sleepstation?lang=en`,
                },
              ],
            },
            footerNav: {
              primary: [BACK_TO_DASHBOARD_LINK],
              secondary: [BACK_TO_OPTIONS_LINK],
            },
          },
        },
      },
    },
    {
      route: `/feedback/end-of-therapy`,
      data: {
        page: {
          content: {
            complete: {
              urlPositive: FEEDBACK_END_OF_THERAPY_THANK_YOU_URL,
              urlDefault: FEEDBACK_THANK_YOU_URL,
            },
          },
        },
      },
    },
    {
      route: `feedback/end-of-therapy/thank-you`,
      data: {
        page: {
          content: {
            title: `Thank you!`,
            // Store the string version to transmit to the server
            body: FEEDBACK_END_OF_THERAPY_CONSENT_STATEMENT,
            bodyAst: parseToAst(FEEDBACK_END_OF_THERAPY_CONSENT_STATEMENT),
            contactMe: {
              button: {
                label: `Yes, contact me`,
              },
              messages: {
                success: {
                  title: `Thank you`,
                  body: `You’ll be contacted by a member of our team.`,
                },
                failure: {
                  title: `Something went wrong`,
                  body: `We’re really sorry. Please try again later.`,
                },
              },
            },
            testimonialOptions: {
              title: `or share your experience now`,
              links: [
                {
                  linkLabel: `Video`,
                  linkUrl: `/feedback/video-testimonial?${QUERY_PARAMS.SHOW_SECONDARY_NAV}=1`,
                  imageAlt: `Illustration of a video screen with a woman`,
                  imageUrl: videoTestimonialSvgUrl,
                  trackingId: `video`,
                  dataCy: `video-testimonial-link`,
                },
                {
                  linkLabel: `Written`,
                  linkUrl: `/feedback/written-testimonial?${QUERY_PARAMS.SHOW_SECONDARY_NAV}=1`,
                  imageAlt: `Illustration of a notebook and pencils`,
                  imageUrl: writtenTestimonialSvgUrl,
                  trackingId: `written`,
                  dataCy: `written-testimonial-link`,
                },
              ],
            },
            footerNav: {
              primary: [BACK_TO_DASHBOARD_LINK],
            },
          },
        },
      },
    },
    {
      route: `/hub`,
      data: {
        page: {
          content: {
            referralRequestProcessedBanner: {
              id: `referral-request-processed-banner`,
              title: `Your request for NHS access has been accepted`,
              bodyAst: parseToAst(
                `Congratulations! You’ve been accepted into our Sleep Transformation Programme with free NHS access. You can start right away.\n\nWe’ll start by asking you some questions. They take most people about 10 minutes to answer. Your answers will help us pick the best people to support you.\n\nWe’re excited to support you on this journey to better sleep, just as we’ve helped thousands of others.`
              ),
              link: {
                label: `Start now`,
                // URL is injected
              },
            },
            notices: {
              [NOTICE_TYPE.WELCOME]: {
                title: `Welcome to Sleepstation!`,
                bodyTemplate: `Hello #{chosenName} and welcome to Sleepstation! Here we’ll help you understand your sleep, equip you with the best information about how to get a good night’s sleep, keep you up to date on the latest sleep research and even take you on our full sleep improvement programme if you’re struggling with your sleep. Take a look around!`,
              },
              [NOTICE_TYPE.REJECTED]: {
                title: `Your assessment result`,
                bodyTemplate: `Thank you for completing the self-assessment questions.

From what you’ve told us so far about other health conditions that you have, it doesn’t sound like the sleep improvement programme would be suitable for you. We understand this may be disappointing, but our programme is based around a technique that may make you feel worse so it’s important that we let you know this as soon as possible.

We’d recommend speaking to your doctor to find out what other options are available for you in your area, as face-to-face support would likely be the best option for you.

You’ll still have access to our sleep coaches and library.`,
              },
            },
            welcomeMessageTemplate: `Welcome, #{chosenName}`,
            sections: [
              {
                id: `access`,
                title: ``,
                cards: [
                  {
                    id: `referral-request`,
                    variant: HUB_CARD_VARIANT.INFO_IMAGE,
                    title: `We’ve received your request for NHS access`,
                    shouldShowLink: false,
                    primary: {
                      content: `We’ll contact your GP to confirm that you're registered with the GP practice you selected.`,
                      subContent: `When we have confirmation we’ll be able to grant you access. We'll keep you updated by email.`,
                    },
                    secondary: {
                      image: {
                        default: {
                          url: nhsLogoUrl,
                          alt: `NHS`,
                          aspectRatio: 57 / 23,
                        },
                        mediumUp: {
                          url: nhsLogoUrl,
                          alt: `NHS`,
                          aspectRatio: 97 / 39,
                        },
                      },
                    },
                    link: {
                      label: ``,
                      // URL is injected
                    },
                  },
                ],
              },
              {
                id: `message-us`,
                title: `Connect with an expert`,
                cards: [
                  {
                    id: `sleep-coaches`,
                    variant: HUB_CARD_VARIANT.IMAGE_CTA,
                    title: `Our team are here to help`,
                    primary: {
                      bodyAst: parseToAst(
                        `Whether you're curious about sleep science or struggling to sleep, we've got you covered. Our expert team have the knowledge and expertise to support you. Message them for answers to your queries, expert guidance and tailored solutions to better sleep.`
                      ),
                    },
                    secondary: {
                      image: {
                        default: {
                          url: sleepCoachesSmallUrl,
                          alt: `Sleep coaches smiling`,
                          aspectRatio: 200 / 83,
                        },
                        mediumUp: {
                          url: sleepCoachesLargeUrl,
                          alt: `Three sleep coaches smiling`,
                          aspectRatio: 235 / 174,
                        },
                      },
                    },
                    link: {
                      label: `Message our team`,
                      // URL is injected
                    },
                  },
                ],
              },
              {
                id: `about-your-sleep`,
                title: `You and your sleep`,
                columns: [1, 1, 2],
                cards: [
                  {
                    id: `are-you-getting-enough-sleep`,
                    variant: HUB_CARD_VARIANT.LINK,
                    title: `Are you getting enough zzzs?`,
                    content: `That afternoon slump — sometimes it’s hard to tell if it’s just normal fatigue or a sign of a sleep issue. Excessive daytime sleepiness can really impact your daily life. Check your sleepiness score today.`,
                    link: {
                      label: `See how you score`,
                      url: `/hub/are-you-getting-enough-sleep/intro`,
                    },
                    resultsLink: {
                      label: `View results`,
                      url: `/hub/are-you-getting-enough-sleep/results`,
                    },
                    image: {
                      url: enoughSleepSvgUrl,
                      alt: `Person falling asleep in chair`,
                    },
                  },
                  {
                    id: `what-do-you-know-about-sleep`,
                    variant: HUB_CARD_VARIANT.LINK,
                    title: `Could your thoughts be keeping you awake?`,
                    content: `Your thoughts and beliefs about sleep can play a big part in how well you sleep. Discover whether your beliefs are based on fact or myth and whether they may be impacting your sleep.`,
                    image: { url: thoughtsSvgUrl, alt: `Thought bubbles` },
                    link: {
                      label: `Find out your score`,
                      url: `/hub/what-do-you-know-about-sleep/intro`,
                    },
                    resultsLink: {
                      label: `View results`,
                      url: `/hub/what-do-you-know-about-sleep/results`,
                    },
                  },
                ],
              },
              {
                id: `sleep-library`,
                title: `Sleep library`,
                columns: [1, 1, 2],
                cards: [
                  {
                    id: `sleep-library`,
                    variant: HUB_CARD_VARIANT.LINK,
                    title: `Expertly curated content`,
                    content: `Your go-to base for all things sleep-related. Expand your knowledge about sleep with exercises, self-help guides and more from our team of researchers and sleep experts.`,
                    subContent: `New content will be added quarterly.`,
                    link: {
                      label: `Explore current resources`,
                      url: `/resources/`,
                    },
                    image: {
                      url: cardLibraryWebpUrl,
                      alt: `Books and headphones with smiling brain`,
                    },
                  },
                ],
              },
              {
                id: `need-help`,
                title: `Structured sleep support`,
                cards: [
                  {
                    id: `sleep-improvement-programme`,
                    variant: HUB_CARD_VARIANT.DUAL_COLUMN_CTA,
                    title: `Sleepstation CBTi is a digital programme designed to help you overcome long-term sleep problems.`,
                    primary: {
                      bodyAst: parseToAst(
                        `Using scientifically-backed methods and personally tailored support from human sleep coaches, we’ll help you look forward to bedtime. Sleepstation CBTi can help you:`
                      ),
                      bodyAstPrivateGrant: parseToAst(
                        `Our NHS service isn’t available at your GP practice but we can still help by offering you a non-means tested grant towards a place on our programme.\n\nUsing scientifically-backed methods and personally tailored support, we’ll help you look forward to bedtime, knowing you can:`
                      ),
                    },
                    secondary: {
                      bullets: {
                        icon: {
                          name: `included`,
                          type: `positive`,
                        },
                        values: [
                          {
                            value: `Fall asleep quickly`,
                          },
                          {
                            name: `included`,
                            value: `Get all the sleep you need`,
                          },
                          {
                            name: `included`,
                            value: `Spend less time awake in bed`,
                          },
                          {
                            name: `included`,
                            value: `Wake feeling refreshed`,
                          },
                        ],
                      },
                    },
                    image: {
                      url: cardProgrammeSvgUrl,
                      alt: `A couple sleeping in bed`,
                      aspectRatio: 431 / 113,
                    },
                    link: {
                      label: `Find out more`,
                      url: `/hub/about-therapy`,
                    },
                    linkNhs: {
                      label: `Find out more`,
                      url: `/hub/about-nhs-therapy`,
                    },
                    linkPrivate: {
                      label: `Find out more`,
                      url: `/hub/buy-therapy`,
                    },
                    linkPrivateWithAccess: {
                      label: `Find out more`,
                      url: `/hub/access-therapy`,
                    },
                    linkPrivateGrant: {
                      prologue: `You can claim a £135 grant today.`,
                      label: `Find out more`,
                      url: `/hub/buy-therapy-grant`,
                    },
                  },
                ],
              },
            ],
          },
        },
      },
    },
    {
      route: `/hub/about-therapy`,
      data: {
        page: {
          content: {
            title: `Sleep well, live better`,
            benefitsCard: {
              image: {
                url: bedroomIllustrationUrl,
                alt: `Lady stretching her arms up in bed`,
              },
              primary: {
                bullets: {
                  icon: {
                    name: `included`,
                    type: `positive`,
                  },
                  values: [
                    {
                      value: `Fall asleep faster`,
                    },
                    {
                      name: `included`,
                      value: `Stay asleep longer`,
                    },
                    {
                      name: `included`,
                      value: `Get better quality sleep`,
                    },
                    {
                      name: `included`,
                      value: `Have less disrupted sleep`,
                    },
                    {
                      name: `included`,
                      value: `Feel more energised`,
                    },
                    {
                      name: `included`,
                      value: `Boost your mood`,
                    },
                  ],
                },
              },
            },
            infoPill: {
              title: `Your organisation has paid for your access`,
              iconName: `ratingStar`,
            },
            subsectionCopy: `With a simple two-step approach, we’ll help you to understand exactly what’s stopping you from sleeping well and work with you to deliver a realistic plan to give you the sleep you deserve.`,
            video: {
              introTitle: `It's time to reclaim your sleep`,
              introAst: parseToAst(
                `In the video below Christina explains what you can expect from the support team behind our digital sleep clinic.`
              ),
              video: {
                provider: VIDEO_PROVIDERS.VIMEO,
                id: `891496499?h=98426d567a`,
                aspectRatio: 16 / 9,
              },
            },
            infoCards: {
              cards: [
                {
                  title: `1. Review your sleep`,
                  bodyAst: parseToAst(
                    `- Uncover the real reasons why you’re not sleeping well with an in-depth, week-long sleep review.\n- Meet your sleep coaches. They’ll help, advise and guide you at every step, making sure you stay on the right track until you’re sleeping soundly.\n- Tell us about your sleep problems, how they affect you day-to-day and what you’d like to change. We’ll take the time to get to know you to help you reach your goals.\n- Gain important insights from your sleep review report. At the end of this week, you’ll receive a summary and recommended next steps to improve your sleep.`
                  ),
                },
                {
                  title: `2. Repair your sleep`,
                  bodyAst: parseToAst(
                    `- Start seeing results by following your tailored, multi-step sleep plan, targeting your exact sleep problems and your specific goals.\n- Experience deeper sleep, week-by-week, as you discover new, lifelong skills and techniques to resolve your sleep problems.\n- Stay on track with support from your sleep coaches. They’ll be on hand to discuss how you’re doing, monitor your progress and offer any further advice, guidance or motivation you may need.`
                  ),
                },
              ],
            },
            patientShortStories: {
              title: `We help thousands of people to sleep better every month`,
              body: `How would restored sleep change your life? This is how it changed for some of our users:`,
              stories: patientShortStoriesData,
            },
            ctaButton: {
              labelHasNotStarted: `Let’s get started`,
              labelHasStarted: `Continue`,
              // URL is injected
            },
            ctaButtonSecondary: {
              labelHasNotStarted: `Solve your sleep problem`,
              labelHasStarted: `Continue`,
              // URL is injected
            },
          },
        },
      },
    },
    {
      route: `/hub/access-therapy`,
      data: {
        page: {
          content: {
            title: `Sleep well, live better`,
            benefitsCard: {
              image: {
                url: bedroomIllustrationUrl,
                alt: `Lady stretching her arms up in bed`,
              },
              primary: {
                bullets: {
                  icon: {
                    name: `included`,
                    type: `positive`,
                  },
                  values: [
                    {
                      value: `Fall asleep faster`,
                    },
                    {
                      name: `included`,
                      value: `Stay asleep longer`,
                    },
                    {
                      name: `included`,
                      value: `Get better quality sleep`,
                    },
                    {
                      name: `included`,
                      value: `Have less disrupted sleep`,
                    },
                    {
                      name: `included`,
                      value: `Feel more energised`,
                    },
                    {
                      name: `included`,
                      value: `Boost your mood`,
                    },
                  ],
                },
              },
            },
            subsectionCopy: `With a simple two-step approach, we’ll help you to understand exactly what’s stopping you from sleeping well and work with you to deliver a realistic plan to give you the sleep you deserve.`,
            video: {
              introTitle: `It's time to reclaim your sleep`,
              introAst: parseToAst(
                `In the video below Christina explains what you can expect from the support team behind our digital sleep clinic.`
              ),
              video: {
                provider: VIDEO_PROVIDERS.VIMEO,
                id: `891496499?h=98426d567a`,
                aspectRatio: 16 / 9,
              },
            },
            infoCards: {
              cards: [
                {
                  title: `1. Review your sleep`,
                  bodyAst: parseToAst(
                    `- Uncover the real reasons why you’re not sleeping well with an in-depth, week-long sleep review.\n- Meet your sleep coaches. They’ll help, advise and guide you at every step, making sure you stay on the right track until you’re sleeping soundly.\n- Tell us about your sleep problems, how they affect you day-to-day and what you’d like to change. We’ll take the time to get to know you to help you reach your goals.\n- Gain important insights from your sleep review report. At the end of this week, you’ll receive a summary and recommended next steps to improve your sleep.`
                  ),
                },
                {
                  title: `2. Repair your sleep`,
                  bodyAst: parseToAst(
                    `- Start seeing results by following your tailored, multi-step sleep plan, targeting your exact sleep problems and your specific goals.\n- Experience deeper sleep, week-by-week, as you discover new, lifelong skills and techniques to resolve your sleep problems.\n- Stay on track with support from your sleep coaches. They’ll be on hand to discuss how you’re doing, monitor your progress and offer any further advice, guidance or motivation you may need.`
                  ),
                },
              ],
            },
            patientShortStories: {
              title: `We help thousands of people to sleep better every month`,
              body: `How would restored sleep change your life? This is how it changed for some of our users:`,
              stories: patientShortStoriesData,
            },
            ctaButton: {
              labelHasNotStarted: `Let’s get started`,
              labelHasStarted: `Let’s get started`,
              // URL is injected
            },
            ctaButtonSecondary: {
              labelHasNotStarted: `Solve your sleep problem`,
              labelHasStarted: `Solve your sleep problem`,
              // URL is injected
            },
          },
        },
      },
    },
    {
      route: `/hub/about-nhs-therapy`,
      data: {
        page: {
          content: {
            title: `Sleep well, live better`,
            benefitsCard: {
              image: {
                url: bedroomIllustrationUrl,
                alt: `Lady stretching her arms up in bed`,
              },
              primary: {
                bullets: {
                  icon: {
                    name: `included`,
                    type: `positive`,
                  },
                  values: [
                    {
                      value: `Fall asleep faster`,
                    },
                    {
                      name: `included`,
                      value: `Stay asleep longer`,
                    },
                    {
                      name: `included`,
                      value: `Get better quality sleep`,
                    },
                    {
                      name: `included`,
                      value: `Have less disrupted sleep`,
                    },
                    {
                      name: `included`,
                      value: `Feel more energised`,
                    },
                    {
                      name: `included`,
                      value: `Boost your mood`,
                    },
                  ],
                },
              },
            },
            infoPill: {
              title: `Our NHS service is available where you live - secure your free place today`,
              icon: null,
              image: {
                url: nhsLogoUrl,
                alt: `NHS`,
                aspectRatio: 97 / 39,
              },
            },
            infoPillAccessPending: {
              title: `Your request is being processed`,
              icon: null,
              image: {
                url: nhsLogoUrl,
                alt: `NHS`,
                aspectRatio: 97 / 39,
              },
            },
            subsectionCopy: `With a simple two-step approach, we’ll help you to understand exactly what’s stopping you from sleeping well and work with you to deliver a realistic plan to give you the sleep you deserve.`,
            video: {
              introTitle: `It's time to reclaim your sleep`,
              introAst: parseToAst(
                `In the video below Christina explains what you can expect from the support team behind our digital sleep clinic.`
              ),
              video: {
                provider: VIDEO_PROVIDERS.VIMEO,
                id: `891496499?h=98426d567a`,
                aspectRatio: 16 / 9,
              },
            },
            infoCards: {
              cards: [
                {
                  title: `1. Review your sleep`,
                  bodyAst: parseToAst(
                    `- Uncover the real reasons why you’re not sleeping well with an in-depth, week-long sleep review.\n- Meet your sleep coaches. They’ll help, advise and guide you at every step, making sure you stay on the right track until you’re sleeping soundly.\n- Tell us about your sleep problems, how they affect you day-to-day and what you’d like to change. We’ll take the time to get to know you to help you reach your goals.\n- Gain important insights from your sleep review report. At the end of this week, you’ll receive a summary and recommended next steps to improve your sleep.`
                  ),
                },
                {
                  title: `2. Repair your sleep`,
                  bodyAst: parseToAst(
                    `- Start seeing results by following your tailored, multi-step sleep plan, targeting your exact sleep problems and your specific goals.\n- Experience deeper sleep, week-by-week, as you discover new, lifelong skills and techniques to resolve your sleep problems.\n- Stay on track with support from your sleep coaches. They’ll be on hand to discuss how you’re doing, monitor your progress and offer any further advice, guidance or motivation you may need.`
                  ),
                },
              ],
            },
            patientShortStories: {
              title: `We help thousands of people to sleep better every month`,
              body: `How would restored sleep change your life? This is how it changed for some of our users:`,
              stories: patientShortStoriesData,
            },
            ctaButton: {
              labelHasNotStarted: `Request NHS access`,
              labelHasStarted: `Continue`,
              // URL is injected
            },
            ctaButtonSecondary: {
              labelHasNotStarted: `Request NHS access`,
              labelHasStarted: `Continue`,
              // URL is injected
            },
          },
        },
      },
    },
    {
      route: `/hub/buy-therapy`,
      data: {
        page: {
          content: {
            title: `Sleep well, live better`,
            benefitsCard: {
              image: {
                url: bedroomIllustrationUrl,
                alt: `Lady stretching her arms up in bed`,
              },
              primary: {
                bullets: {
                  icon: {
                    name: `included`,
                    type: `positive`,
                  },
                  values: [
                    {
                      value: `Fall asleep faster`,
                    },
                    {
                      name: `included`,
                      value: `Stay asleep longer`,
                    },
                    {
                      name: `included`,
                      value: `Get better quality sleep`,
                    },
                    {
                      name: `included`,
                      value: `Have less disrupted sleep`,
                    },
                    {
                      name: `included`,
                      value: `Feel more energised`,
                    },
                    {
                      name: `included`,
                      value: `Boost your mood`,
                    },
                  ],
                },
              },
            },
            subsectionCopy: `With a simple two-step approach, we’ll help you to understand exactly what’s stopping you from sleeping well and work with you to deliver a realistic plan to give you the sleep you deserve.`,
            video: {
              introTitle: `It's time to reclaim your sleep`,
              introAst: parseToAst(
                `Discover our uniquely effective model that delivers lasting results for even the most severe sleep problems. We'll use cutting-edge science to target what's really keeping you awake. With our support, you'll retrain your mind and body to embrace sleep.\n\nIn the video below Christina explains what you can expect from the support team behind our Sleep Transformation Programme.`
              ),
              video: {
                provider: VIDEO_PROVIDERS.VIMEO,
                id: `891496499?h=98426d567a`,
                aspectRatio: 16 / 9,
              },
            },
            infoCards: {
              cards: [
                {
                  title: `1. Review your sleep`,
                  bodyAst: parseToAst(
                    `- Uncover the real reasons why you’re not sleeping well with an in-depth, week-long sleep review.\n- Meet your sleep coaches. They’ll help, advise and guide you at every step, making sure you stay on the right track until you’re sleeping soundly.\n- Tell us about your sleep problems, how they affect you day-to-day and what you’d like to change. We’ll take the time to get to know you to help you reach your goals.\n- Gain important insights from your sleep review report. At the end of this week, you’ll receive a summary and recommended next steps to improve your sleep.`
                  ),
                },
                {
                  title: `2. Repair your sleep`,
                  bodyAst: parseToAst(
                    `- Start seeing results by following your tailored, multi-step sleep plan, targeting your exact sleep problems and your specific goals.\n- Experience deeper sleep, week-by-week, as you discover new, lifelong skills and techniques to resolve your sleep problems.\n- Stay on track with support from your sleep coaches and our wider team of experts. They’ll be on hand to discuss how you’re doing, monitor your progress and offer any further advice, guidance or motivation you may need.`
                  ),
                },
              ],
            },
            patientShortStories: {
              title: `We help thousands of people to sleep better every month`,
              body: `How would restored sleep change your life? This is how it changed for other people who have tried our programme:`,
              stories: patientShortStoriesData,
            },
            ctaButton: {
              // labelHasNotStarted is injected (with price),
              labelHasStarted: `Continue`,
              // URL is injected
            },
            ctaButtonSecondary: {
              labelHasNotStarted: `Help me fix my sleep`,
              labelHasStarted: `Continue`,
              // URL is injected
            },
          },
        },
      },
    },
    {
      route: `/hub/buy-therapy-grant`,
      data: {
        page: {
          content: {
            title: `Sleep well, live better`,
            benefitsCard: {
              image: {
                url: bedroomIllustrationUrl,
                alt: `Lady stretching her arms up in bed`,
              },
              primary: {
                bullets: {
                  icon: {
                    name: `included`,
                    type: `positive`,
                  },
                  values: [
                    {
                      value: `Fall asleep faster`,
                    },
                    {
                      name: `included`,
                      value: `Stay asleep longer`,
                    },
                    {
                      name: `included`,
                      value: `Get better quality sleep`,
                    },
                    {
                      name: `included`,
                      value: `Have less disrupted sleep`,
                    },
                    {
                      name: `included`,
                      value: `Feel more energised`,
                    },
                    {
                      name: `included`,
                      value: `Boost your mood`,
                    },
                  ],
                },
              },
            },
            infoPill: {
              title: `Secure your place today and save £135!`,
              iconName: `ratingStar`,
              description: `You’re eligible for a grant towards support fees. Secure your spot now and save £135 on the programme. Don't miss out on this fantastic opportunity!`,
            },
            subsectionCopy: `With a simple two-step approach, we’ll help you to understand exactly what’s stopping you from sleeping well and work with you to deliver a realistic plan to give you the sleep you deserve.`,
            video: {
              introTitle: `It’s time to reclaim your sleep`,
              introAst: parseToAst(
                `Discover our uniquely effective model that delivers lasting results for even the most severe sleep problems. We'll use cutting-edge science to target what's really keeping you awake. With our support, you'll retrain your mind and body to embrace sleep.\n\nIn the video below Christina explains what you can expect from the support team behind our Sleep Transformation Programme.`
              ),
              video: {
                provider: VIDEO_PROVIDERS.VIMEO,
                id: `891496499?h=98426d567a`,
                aspectRatio: 16 / 9,
              },
            },
            infoCards: {
              cards: [
                {
                  title: `1. Review your sleep`,
                  bodyAst: parseToAst(
                    `- Uncover the real reasons why you’re not sleeping well with an in-depth, week-long sleep review.\n- Meet your sleep coaches. They’ll help, advise and guide you at every step, making sure you stay on the right track until you’re sleeping soundly.\n- Tell us about your sleep problems, how they affect you day-to-day and what you’d like to change. We’ll take the time to get to know you to help you reach your goals.\n- Gain important insights from your sleep review report. At the end of this week, you’ll receive a summary and recommended next steps to improve your sleep.`
                  ),
                },
                {
                  title: `2. Repair your sleep`,
                  bodyAst: parseToAst(
                    `- Start seeing results by following your tailored, multi-step sleep plan, targeting your exact sleep problems and your specific goals.\n- Experience deeper sleep, week-by-week, as you discover new, lifelong skills and techniques to resolve your sleep problems.\n- Stay on track with support from your sleep coaches and our wider team of experts. They’ll be on hand to discuss how you’re doing, monitor your progress and offer any further advice, guidance or motivation you may need.`
                  ),
                },
              ],
            },
            patientShortStories: {
              title: `We help thousands of people to sleep better every month`,
              body: `How would restored sleep change your life? This is how it changed for other people who have tried our programme:`,
              stories: patientShortStoriesData,
            },
            ctaButton: {
              labelHasNotStarted: `Claim £135 grant`,
              labelHasStarted: `Continue`,
              // URL is injected
            },
            ctaButtonSecondary: {
              labelHasNotStarted: `Claim £135 grant`,
              labelHasStarted: `Continue`,
              // URL is injected
            },
          },
        },
      },
    },
    {
      route: `/hub/what-do-you-know-about-sleep/intro`,
      data: {
        page: {
          content: {
            title: `Could your thoughts be keeping you awake?`,
            body: `The **Dysfunctional Beliefs and Attitudes About Sleep (DBAS-16)** is a validated questionnaire that is used to evaluate beliefs, expectations, and attitudes related to sleep. It helps identify unhelpful thoughts and misconceptions that may impact your sleep.\n\n You’ll answer 16 questions about your sleep experiences and how think and feel about sleep.`,
            takeQuestionnaireButton: {
              label: `Get your score`,
              url: `/hub/what-do-you-know-about-sleep/questions`,
            },
            image: {
              url: dbasIntroImageUrl,
              alt: `Woman reading in bed`,
              aspectRatio: 234 / 169,
            },
          },
        },
      },
    },
    {
      route: `/hub/what-do-you-know-about-sleep/results`,
      data: {
        page: {
          content: {
            titleTemplate: `You scored #{score}`,
            scoreTemplate: `#{score}/#{maximumScore}`,
            buttonText: `You can retake this test again to compare how your beliefs and attitudes change over time.`,
            retakeQuestionnaireButton: {
              label: `Retake test`,
              url: `/hub/what-do-you-know-about-sleep/intro`,
            },
            explanation: {
              [RANGE_DBAS.LOW]: parseToAst(
                `Your results indicate that you have realistic beliefs about sleep and a healthy attitude towards sleep.`
              ),
              [RANGE_DBAS.LOW_FLAGGED]: parseToAst(
                `Your score shows that you have mostly reasonable expectations and thoughts about your sleep.`
              ),
              [RANGE_DBAS.HIGH]: parseToAst(
                `Your results indicate that you may have some unrealistic beliefs and/or unhealthy attitudes about sleep. Our team can help you to identity any problems and provide support, education, coping strategies and effective tools to help you to shift your mindset, which can significantly improve your sleep quality.`
              ),
            },
            beliefs: {
              title: {
                [RANGE_DBAS.LOW_FLAGGED]: parseToAst(
                  `However, your answers did indicate strong belief in the following:`
                ),
                [RANGE_DBAS.HIGH]: parseToAst(
                  `It’s important to pay attention to any questions where you score greater than 5. Your answers did indicate strong belief in the following:`
                ),
              },
              explanation: {
                [RANGE_DBAS.LOW]: parseToAst(
                  `Realistic expectations and a positive attitude towards sleep provide a solid foundation for great sleep.\n\nIf you’re having sleep problems, reach out to our sleep coaches to find out how they can help.`
                ),
                [RANGE_DBAS.LOW_FLAGGED]: parseToAst(
                  `Realistic expectations and a positive attitude towards sleep provide a solid foundation for great sleep.\n\nIf you’re having sleep problems, reach out to our sleep coaches to find out how they can help.`
                ),
                [RANGE_DBAS.HIGH]: parseToAst(
                  `One way of thinking about the reasonableness of our beliefs is to see how well they match the facts. There is a self-help tool in the resources section that explains how to do this.\n\nStart by challenging these thoughts - checkout the self-help tool in our resources section. If you need more help, reach out to our sleep coaches.`
                ),
              },
            },
            sleepCoachesCard: {
              id: `sleep-coaches`,
              variant: HUB_CARD_VARIANT.IMAGE_CTA,
              title: `Our sleep coaches are here to help you`,
              primary: {
                bodyAst: parseToAst(
                  `Our team have the knowledge and expertise to support you.`
                ),
              },
              secondary: {
                image: {
                  default: {
                    url: sleepCoachesSmallUrl,
                    alt: `Sleep coaches smiling`,
                    aspectRatio: 200 / 83,
                  },
                },
              },
              link: {
                label: `Send us a message`,
                // URL is injected
              },
            },
            resourcesCard: {
              id: `sleep-library`,
              variant: HUB_CARD_VARIANT.LINK,
              title: `Checkout our self-help resources`,
              content: `And if you need more help, message our sleep coaches.`,
              link: {
                label: `Explore the sleep library`,
                url: `/resources/`,
              },
              image: {
                url: cardLibraryWebpUrl,
                alt: `Books and headphones with smiling brain`,
              },
            },
          },
        },
      },
    },
    {
      route: `/hub/are-you-getting-enough-sleep/intro`,
      data: {
        page: {
          content: {
            title: `Are you getting enough zzzs?`,
            body: `The **Epworth Sleepiness Scale (ESS)** is a short self-assessment tool designed to measure daytime sleepiness. It can be used to identify excessive daytime sleepiness, which can be associated with various sleep disorders (including sleep apnoea).\n\nIf you’re experiencing excessive daytime sleepiness, it can impact your overall well-being and daily functioning. The ESS can help identify problems.`,
            takeQuestionnaireButton: {
              label: `Take the sleepiness test`,
              url: `/hub/are-you-getting-enough-sleep/questions`,
            },
            image: {
              url: epworthIntroImageUrl,
              alt: `Lady looks concerned in bed while looking at the time on her alarm clock`,
              aspectRatio: 234 / 169,
            },
          },
        },
      },
    },
    {
      route: `/hub/are-you-getting-enough-sleep/results`,
      data: {
        page: {
          content: {
            titleTemplate: `You scored #{score}`,
            scoreTemplate: `#{score}/#{maximumScore}`,
            description: {
              [RANGE_EPWORTH.LOW]: `If you want tips on maintaining good sleep, check out our sleep library.`,
              [RANGE_EPWORTH.MILD]: `Every morning, a few hours after you wake up, pay attention to how sleepy you feel. If you continue to feel sleepy during the day, and you’ve not been diagnosed with something that would explain these symptoms, you should talk to your doctor.`,
              [RANGE_EPWORTH.MODERATE]: `If you’ve not already been diagnosed with a sleep problem that would explain your score, then it will be important to talk to your doctor. Our sleep coaches can also help.`,
              [RANGE_EPWORTH.HIGH]: `If you’ve not already been diagnosed with a sleep problem that would explain your score, then it will be important to talk to your doctor. Our sleep coaches can also help.`,
            },
            explanation: {
              [RANGE_EPWORTH.LOW]: parseToAst(
                `Your score indicates that your daytime sleepiness levels are within the **normal range.**`
              ),
              [RANGE_EPWORTH.MILD]: parseToAst(
                `Your score indicates that your daytime sleepiness levels are slightly outside of the normal range - defined as **mildly excessive daytime symptoms.**`
              ),
              [RANGE_EPWORTH.MODERATE]: parseToAst(
                `Your score indicates that your daytime sleepiness levels are **moderately excessive** and a score in this range should be reviewed by a doctor.`
              ),
              [RANGE_EPWORTH.HIGH]: parseToAst(
                `Your score indicates that your daytime sleepiness levels are in the **severe range** and a score in this range should be reviewed by a doctor.`
              ),
            },
            therapyCard: {
              id: `sleep-coaches`,
              variant: HUB_CARD_VARIANT.IMAGE_CTA,
              title: `Our expert team are here to help you`,
              primary: {
                bodyAst: parseToAst(
                  `Contact us for answers to your queries, expert guidance and tailored solutions for better sleep.`
                ),
              },
              secondary: {
                image: {
                  default: {
                    url: sleepCoachesSmallUrl,
                    alt: `Sleep coaches smiling`,
                    aspectRatio: 200 / 83,
                  },
                },
              },
              link: {
                label: `Send us a message`,
                // URL is injected
              },
            },
            resourcesCard: {
              id: `sleep-library`,
              variant: HUB_CARD_VARIANT.LINK,
              title: `Expertly curated content`,
              content: `Expand your knowledge about sleep with exercises, self-help guides and more from our team of researchers and sleep experts.`,
              link: {
                label: `Explore current resources`,
                url: `/resources/`,
              },
              image: {
                url: cardLibraryWebpUrl,
                alt: `Books and headphones with smiling brain`,
              },
            },
            buttonText: `You can retake this test again to compare how your sleepiness levels change over time.`,
            retakeQuestionnaireButton: {
              label: `Retake test`,
              url: `/hub/are-you-getting-enough-sleep/intro`,
            },
          },
        },
      },
    },
    {
      route: `/onboarding/welcome`,
      data: {
        page: {
          content: {
            deck: {
              title: `Getting started with Sleepstation`,
              completeLink: {
                label: `Let’s go`,
                url: `/onboarding/interests`,
              },
              skipLink: {
                label: `Skip`,
                url: `/onboarding/interests`,
              },
              nextButton: {
                label: `Next`,
              },
              cards: [
                {
                  title: `Connect with the experts`,
                  bodyAst: parseToAst(
                    `Got sleep questions? Want to improve your sleep? Need advice? Our sleep coaches can provide answers, give you support and connect you with other experts in our team - guiding you to expert resources and tailored solutions for better sleep.`
                  ),
                  illustration: {
                    url: messagingSupportUrl,
                    alt: `Person messaging the sleep coaches`,
                  },
                },
                {
                  title: `Discover what matters`,
                  bodyAst: parseToAst(
                    `Improving your sleep starts with understanding it. In fact, it’s essential.  Awaken your sleep potential and gain in-depth insights into different aspects of your sleep with science-backed analysis tools and evidence-based methods.`
                  ),
                  illustration: {
                    url: notesIllustrationUrl,
                    alt: `Notes files and a mobile phone`,
                  },
                },
                {
                  title: `Explore our sleep resources`,
                  bodyAst: parseToAst(
                    `Unlock the mysteries of sleep using resources you can trust. Created and curated by experts with decades of experience in the field of sleep science. Discover a wide range of topics for people of all ages.`
                  ),
                  illustration: {
                    url: readingIllustrationUrl,
                    alt: `Lady reading in bed`,
                  },
                },
                {
                  title: `Solve your sleep problems`,
                  bodyAst: parseToAst(
                    `Are restless nights keeping you awake? Do you feel like you’ve lost control of your sleep?\n\nExplore the benefits of our life-changing Sleep Transformation Programme, which you can access with no waiting lists from the Hub.`
                  ),
                  illustration: {
                    url: sleepingIllustrationUrl,
                    alt: `Couple sleeping in bed`,
                  },
                },
              ],
            },
          },
        },
      },
    },
    {
      route: `/onboarding/nhs-welcome`,
      data: {
        page: {
          meta: {
            title: `Welcome to our NHS services`,
          },
          content: {
            deck: {
              completeLink: {
                label: `Let’s go`,
                url: `/onboarding/interests`,
              },
              nextButton: {
                label: `Next`,
              },
              cards: [
                {
                  title: `Free sleep support through the NHS`,
                  bodyAst: parseToAst(
                    `This is the NHS-funded Sleepstation service. If you’ve been directed here because sleep problems are affecting your life, you’re in the right place.

Sleepstation helps adults improve sleep and overcome insomnia and ongoing sleep difficulties.`
                  ),
                  illustration: {
                    url: serviceWelcomeIllustrationUrl,
                    alt: `Lady asleep in bed with NHS Logo`,
                  },
                },
                {
                  title: `Support that works for you`,
                  bodyAst: parseToAst(
                    `Some people just want a bit of advice first, or to talk through what’s going on. You can message a sleep coach for that. Others know they have insomnia and want a structured plan. You can start our insomnia programme and work through it step by step over 4-7 weeks, with support from our sleep coaches throughout.`
                  ),
                  illustration: {
                    url: servicesIllustrationUrl,
                    alt: `App being supported by people`,
                  },
                },
                {
                  title: `Help when you need it`,
                  bodyAst: parseToAst(
                    `You can message a sleep coach at any point, whether you want to ask a question, talk things through, or get reassurance along the way. If you choose to start the insomnia programme, your sleep coaches will be there to guide and support you.`
                  ),
                  illustration: {
                    url: supportIllustrationUrl,
                    alt: `Headphones and laptop with sleep resources`,
                  },
                },
                {
                  title: `Self-help options`,
                  bodyAst: parseToAst(
                    `If you’re not sure where to start, our self-help resources are a good first step. They include information, guides and practical tools to help you understand and improve your sleep and make changes at your own pace. You can use them on their own, or alongside sleep coach support or the insomnia programme.`
                  ),
                  illustration: {
                    url: resourcesIllustrationUrl,
                    alt: `Messaging support through app`,
                  },
                },
              ],
            },
          },
        },
      },
    },
    {
      route: `/onboarding/interests`,
      data: {
        page: {
          content: {
            title: `How can we help?`,
            listPrompt: `Tell us why you’re here. We can then highlight the most appropriate content and resources for you.`,
            selectionError: {
              noneSelected: `Please select one or more options`,
            },
            interests: [
              {
                title: `I’m interested in sleep`,
                imageUrl: interestsCardSvgUrl,
                imageUrlSelected: interestsCardSelectedSvgUrl,
              },
              {
                title: `I have a sleep problem`,
                imageUrl: interestsCardSvgUrl,
                imageUrlSelected: interestsCardSelectedSvgUrl,
              },
              {
                title: `I want to know how well I’m sleeping`,
                imageUrl: interestsCardSvgUrl,
                imageUrlSelected: interestsCardSelectedSvgUrl,
              },
              {
                title: `I’m looking for advice for someone else`,
                imageUrl: interestsCardSvgUrl,
                imageUrlSelected: interestsCardSelectedSvgUrl,
              },
            ],
            continueButtonLabel: `Continue`,
            failedSubmissionErrorMessage: `There was a problem submitting your interests. Please try again later`,
          },
        },
      },
    },
    {
      route: `/upgrade`,
      data: {
        page: {
          content: {
            sleepEfficiencyProgressChartSection: {
              title: `Let’s get your sleep back on track`,
              bodyAst: parseToAst(
                `Now that we’ve spent some time reviewing your sleep problem and finding out how it’s affecting you, we can help you overcome it. You’ve taken the first step, so let’s keeping going so that you can get the results you’ve been dreaming of.`
              ),
              chart: {
                legend: `You’re here: Sleep review`,
              },
              footer: {
                titleTemplate: `Sleep better by #{date}`,
                bodyTemplate: `People who continue to the next stage of the program typically gain improvements in their sleep after each step and soon feel the benefits. If you start today you could be sleeping better and feeling more positive about your sleep as soon as **#{date}**.`,
              },
            },
            packageWithFeaturesCtaSectionTop: mergeDeepRight(
              packageWithFeaturesCtaSectionBase,
              {
                cardData: {
                  body: `Get your six-step sleep plan and full access to our expert sleep team to unlock the benefits of great sleep.`,
                },
                title: `What you can expect from us`,
                bodyAst:
                  parseToAst(`Now that we understand a bit more about your sleep problem, we can dedicate ourselves to helping you fix it.

We’ll create a step-by-step sleep improvement plan that is centered around the gold-standard scientific approach to treating sleep problems like yours. We’ll be there, every step of the way, to support and guide you on your journey to better sleep.`),
              }
            ),
            illustratedHeaderSection: {
              title: `How we help improve your sleep`,
              bodyAst:
                parseToAst(`We know sleep and you know you. When we bring these things together the results can be life changing. Much like a diet, some of the theory is fairly simple (maybe even obvious) and you may have heard a lot of it before. What’s difficult is finding out what works for you and sticking with it until you get results. That’s how we can help you.

Our expert sleep team have many decades of experience in sleep science and we help thousands of people sleep better every month.

We’ll take the time to listen to you. We’ll help you tailor the science in a way that will work for you. We’ll monitor your progress and give you a boost when it’s needed.

With our support, you’ll take back control of your sleep.`),
              svgUrlXSmall: howItWorksIllustrationXSmallUrl,
              svgUrlSmallUp: howItWorksIllustrationSmallUpUrl,
              imageAlt: `Illustration of Neil Stanley`,
            },
            timelineSection: {
              timeline: [
                {
                  title: `Discover your sleep needs`,
                  htmlAst: parseToAst(
                    `Your sleep needs are as individual as you are. If we’re focussed on the wrong target we’ll never hit the bullseye. We’ll help you understand your sleep needs and create a step-by-step plan to target them.`
                  ),
                  svgUrl: discoverYourSleepNeedsIllustrationUrl,
                },
                {
                  title: `Reach your sleep goals`,
                  htmlAst: parseToAst(
                    `Fall asleep faster. Stay asleep longer. Get better quality sleep. Feel energized. Boost your mood. Maximize your brain power. Whatever your goal, we can help you get there.`
                  ),
                  svgUrl: improveYourMoodIllustrationUrl,
                },
                {
                  title: `Feel the benefits of great sleep`,
                  htmlAst: parseToAst(
                    `Trust us to guide the way and, within a few short weeks, insomnia could be a thing of the past for you. We’ll show you how to apply the science, taking into account your personal needs and circumstances, to get the best sleep possible. Improved sleep can increase your energy levels, reduce stress, calm anxiety, lift your mood, boost your immunity, improve your brain function and so much more.`
                  ),
                  svgUrl: improveSleepQualityIllustrationUrl,
                },
                {
                  title: `Live better`,
                  htmlAst: parseToAst(
                    `When we sleep well, everything feels better. There really is no limit to the benefits that great sleep can bring. Whatever you’re dreaming of, whether it’s getting a dog, changing job, starting a new relationship, losing weight, starting a family, being more active, learning new skills, being more productive, managing your emotions better, having more energy, taking up a new hobby, socialising more or something else, great sleep can help you reach those goals.`
                  ),
                  svgUrl: liveBetterIllustrationUrl,
                },
              ],
            },
            illustratedCtaSection: {
              title: `Don’t stop now`,
              body: `Sleep problems tend to get worse the longer you leave them so it’s unlikely that this problem will resolve itself without intervention. Don’t sleep on it! You’ll not regret prioritizing your sleep.`,
              imageUrlSmallDown:
                ctaComponentProductShotIllustrationSmallDownUrl,
              imageUrlMediumUp: ctaComponentProductShotIllustrationmediumUpUrl,
              linkLabel: `I’m ready - let’s do this!`,
              linkUrlTemplate: CHECKOUT_LINK_TEMPLATE,
            },
            patientShortStoriesSection: {
              title: `You’re not alone`,
              body: `We help thousands of people from all over the world to sleep better every month. We’ve got a proven track record and thousands of success stories to share. We’ve selected a few to show what you might be missing out on if you decide to stop before we’ve really got started.`,
              data: patientShortStoriesData,
            },
            packageWithFeaturesCtaSectionBottom: mergeDeepRight(
              packageWithFeaturesCtaSectionBase,
              {
                title: `Our promise to you`,
                bodyAst: parseToAst(
                  `We believe that everyone deserves to sleep well and we’ll make it our mission to help you get there. If you put trust in our expertise and follow our six-step plan you’ll be sleeping naturally again in no time.`
                ),
              }
            ),
          },
        },
      },
    },
    {
      route: /\/?resources\/.*/,
      data: {
        page: {
          content: {
            transcription: {
              title: `Transcript`,
            },
          },
        },
      },
    },
    {
      route: `/resources`,
      data: {
        page: {
          content: {
            header: {
              title: `Resources`,
              body: `This page contains additional resources to help you sleep better.`,
            },
            sections: {
              pickedForYou: {
                title: `Picked for you`,
              },
              sessionContent: {
                title: `Session content`,
              },
              sleepInformation: {
                title: `Sleep information`,
              },
            },
          },
        },
      },
    },
    {
      route: `/sleep-review-report`,
      data: {
        page: {
          content: {
            header: {
              titleTemplate: {
                default: `Hi #{displayName}!\nYour sleep review report is ready`,
                forceAccepted: `Hi #{displayName}, \nYou can now continue with the programme.`,
              },
              forceAcceptedParagraph: `Based on the additional information you’ve provided, we think Sleepstation could help you overcome your sleep problem. Please review the information below. If you’re happy to proceed, click continue.`,
              backLink: {
                label: `Back to dashboard`,
                url: DASHBOARD_URL,
              },
            },
            yourSleepPatternsSection: {
              title: `Your sleep patterns`,
              body: `Keeping a sleep diary for a short time can help you understand your sleep problem in more detail. Sleep diaries can also highlight opportunities for improving your sleep. If you continue with Sleepstation, our team will review your sleep diaries every week and provide you with advice and guidance about how to improve your sleep. As your sleep begins to improve, your sleep diaries can provide valuable feedback on your progress towards your sleep goals.`,
              chartTitleTemplatePrefix: `When you did sleep, your average sleep per night was`,
              chartTitleTemplatePrefixAlt: `Your average sleep per night was`,
              selfReflection: {
                intro: `Take a few moments to review your sleep diary. Ask yourself the following questions:`,
                questions: [
                  {
                    title: `What was your best night?`,
                    body: `Can you think of anything you did that made that night different to other nights?`,
                  },
                  {
                    title: `How much sleep do you think you need to feel at your best?`,
                    body: `How does that number of hours compare to how long you usually stay in bed? Do they match?`,
                  },
                  {
                    title: `If you wake during the night, are you being woken up by the same thing repeatedly, or do the reasons vary a lot?`,
                    body: `If it’s the same thing often, is there something you could do about that?`,
                  },
                  {
                    title: `What time do you usually wake up on weekdays or workdays?`,
                    body: `How different is this time from your preferred wake time? Do you feel refreshed when you wake?`,
                  },
                  {
                    title: `What time do you usually wake up on weekends or days off?`,
                    body: `If these times differ a lot to other days, what do you think is the reason for the difference?`,
                  },
                  {
                    title: `Is every night the same?`,
                    body: `Compare how you slept each night to how you felt about your sleep that morning and how you feel during the day.`,
                  },
                ],
              },
            },
            sleepAndEmotionsSection: {
              title: `Sleep and your emotions`,
              subtitle: `How we feel is linked to how we sleep`,
              description: `When we’re feeling low or anxious sleep can be disrupted, and people with sleep problems often worry about their sleep. A vicious cycle can develop. The more we worry or think about sleep, the less we sleep. Too much stress can also cause us to feel anxious and anxiety can keep us awake.\n\nBreaking this cycle is an important part of treating sleep problems. It’s very helpful to review how we feel to understand if these factors could be contributing to our sleep problems. Take a few moments to review your results below and have a think about whether anxiety, depression or stress could be causing or contributing to your problem.\n\nThese scores are calculated using the answers you gave to the questions we asked you when you joined Sleepstation. Those questions were part of a validated questionnaire called The Depression, Anxiety and Stress Scale (DASS-21).\n\nWhile your scores can show us where there might be problems, **the DASS-21 is not a diagnostic tool.** It is predominantly used for screening and is aimed at assessing the perceived severity of symptoms related to depression, anxiety and stress. So a high score for depression (for example) doesn’t, necessarily, mean that you’re depressed.`,
              illustrationSvg: SleepAndEmotionsSectionIllustrationSVG,
            },
            resultsSection: {
              primaryCta: {
                title: `Your first session of sleep improvement advice has been prepared for you.`,
              },

              secondaryCtas: {
                questions: {
                  title: `Got a question?`,
                  body: `If you’re not sure what to do next or have any questions <a href="#{appChatMessagesRoute}" target="_self">get in touch</a>.`,
                },
                download: {
                  title: `Self-help guide`,
                  body: `You can now download your self-help guide to better sleep.`,
                  link: {
                    label: `Download guide`,
                    icon: `pdf`,
                    // TODO: Extract to routing
                    url: `#{railsApp}/guides/self_help.pdf`,
                  },
                },
              },
            },
          },
        },
      },
    },
  ],
}

export default CONTENT
